<script setup>
/* global axios */
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilters from './components/ReportFilters.vue';

const route = useRoute();
const store = useStore();
const accountId = route.params.accountId;

const from = ref(0);
const to = ref(0);
const inboxId = ref('');
const page = ref(1);
const perPage = 25;

const rows = ref([]);
const total = ref(0);
const isLoading = ref(false);
let pollTimer = null;

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / perPage)));

const COLUMNS = [
  { key: 'created_at', label: 'التاريخ', w: '9%' },
  { key: 'contact_name', label: 'العميل', w: '10%' },
  { key: 'inbox_name', label: 'صندوق الوارد', w: '9%' },
  { key: 'client_need', label: 'ما يحتاجه العميل', w: '17%' },
  { key: 'ai_answer', label: 'إجابة الذكاء الاصطناعي', w: '17%' },
  { key: 'handoff_message', label: 'رسالة التحويل', w: '17%' },
  { key: 'reason', label: 'السبب', w: '17%' },
];

const conversationUrl = id =>
  frontendURL(`accounts/${accountId}/conversations/${id}`);

const fmtDate = epoch => {
  if (!epoch) return '—';
  return new Date(epoch * 1000).toLocaleString();
};

const clearPoll = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};

const fetchData = async () => {
  if (!from.value || !to.value) return;
  isLoading.value = true;
  try {
    const { data } = await axios.get(
      `/api/v2/accounts/${accountId}/reports/ai_handoffs`,
      {
        params: {
          since: from.value,
          until: to.value,
          inbox_id: inboxId.value || undefined,
          page: page.value,
          per_page: perPage,
        },
      }
    );
    rows.value = data.rows || [];
    total.value = data.meta?.total || 0;
    // some rows are still being summarized in the background — refresh shortly.
    clearPoll();
    if ((data.meta?.pending || 0) > 0) {
      pollTimer = setTimeout(fetchData, 7000);
    }
  } catch (error) {
    useAlert('تعذّر تحميل التقرير');
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = ({ from: f, to: t }) => {
  from.value = f;
  to.value = t;
  page.value = 1;
  fetchData();
};

watch([inboxId], () => {
  page.value = 1;
  fetchData();
});

const goToPage = p => {
  if (p < 1 || p > totalPages.value) return;
  page.value = p;
  fetchData();
};

const cellValue = (row, key) => {
  if (key === 'created_at') return fmtDate(row.created_at);
  if (row.pending && ['client_need', 'ai_answer', 'handoff_message', 'reason'].includes(key)) {
    return '⏳ جاري التحليل…';
  }
  return row[key] || '—';
};

const exportCsv = async () => {
  try {
    const { data } = await axios.get(
      `/api/v2/accounts/${accountId}/reports/ai_handoffs`,
      {
        params: {
          since: from.value,
          until: to.value,
          inbox_id: inboxId.value || undefined,
          page: 1,
          per_page: 5000,
        },
      }
    );
    const headers = ['Conversation', ...COLUMNS.map(c => c.label)];
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [headers.map(esc).join(',')];
    (data.rows || []).forEach(r => {
      lines.push(
        [
          r.id,
          fmtDate(r.created_at),
          r.contact_name,
          r.inbox_name,
          r.client_need,
          r.ai_answer,
          r.handoff_message,
          r.reason,
        ]
          .map(esc)
          .join(',')
      );
    });
    const blob = new Blob(['﻿' + lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ai-handoffs-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    useAlert('تعذّر تصدير التقرير');
  }
};

onBeforeUnmount(clearPoll);
</script>

<template>
  <ReportHeader :header-title="$t('AI_HANDOFFS_REPORT.HEADER')" />
  <div class="flex flex-col gap-4">
    <ReportFilters
      :show-entity-filter="false"
      :show-group-by="false"
      :show-business-hours="false"
      @filter-change="onFilterChange"
    />

    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex items-center gap-2">
        <label class="text-sm text-n-slate-11">
          {{ $t('AI_HANDOFFS_REPORT.INBOX') }}
        </label>
        <select
          v-model="inboxId"
          class="h-9 px-2 text-sm rounded-lg bg-n-alpha-black1 border border-n-weak text-n-slate-12"
        >
          <option value="">{{ $t('AI_HANDOFFS_REPORT.ALL_INBOXES') }}</option>
          <option v-for="ib in inboxes" :key="ib.id" :value="ib.id">
            {{ ib.name }}
          </option>
        </select>
        <span class="text-sm text-n-slate-11">
          {{ $t('AI_HANDOFFS_REPORT.TOTAL', { count: total }) }}
        </span>
      </div>
      <button
        class="h-9 px-3 text-sm rounded-lg bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4"
        :disabled="!rows.length"
        @click="exportCsv"
      >
        {{ $t('AI_HANDOFFS_REPORT.EXPORT') }}
      </button>
    </div>

    <div class="w-full overflow-hidden border rounded-lg border-n-weak">
      <table class="w-full text-xs text-right table-fixed" dir="rtl">
        <colgroup>
          <col style="width: 3.5rem" />
          <col v-for="col in COLUMNS" :key="col.key" :style="{ width: col.w }" />
        </colgroup>
        <thead class="bg-n-slate-3 text-n-slate-11">
          <tr>
            <th class="px-2 py-2 font-medium">#</th>
            <th
              v-for="col in COLUMNS"
              :key="col.key"
              class="px-2 py-2 font-medium whitespace-nowrap"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading && !rows.length">
            <td :colspan="COLUMNS.length + 1" class="px-3 py-6 text-center text-n-slate-11">
              {{ $t('AI_HANDOFFS_REPORT.LOADING') }}
            </td>
          </tr>
          <tr v-else-if="!rows.length">
            <td :colspan="COLUMNS.length + 1" class="px-3 py-6 text-center text-n-slate-11">
              {{ $t('AI_HANDOFFS_REPORT.EMPTY') }}
            </td>
          </tr>
          <tr
            v-for="row in rows"
            :key="row.id"
            class="align-top border-t border-n-weak"
          >
            <td class="px-2 py-2 align-top">
              <a
                :href="conversationUrl(row.id)"
                class="text-n-brand hover:underline"
                dir="ltr"
              >
                #{{ row.id }}
              </a>
            </td>
            <td
              v-for="col in COLUMNS"
              :key="col.key"
              class="px-2 py-2 align-top break-words whitespace-normal leading-relaxed"
            >
              {{ cellValue(row, col.key) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="totalPages > 1" class="flex items-center justify-center gap-2">
      <button
        class="h-8 px-3 text-sm rounded-md bg-n-slate-3 disabled:opacity-40"
        :disabled="page <= 1"
        @click="goToPage(page - 1)"
      >
        {{ $t('AI_HANDOFFS_REPORT.PREV') }}
      </button>
      <span class="text-sm text-n-slate-11">{{ page }} / {{ totalPages }}</span>
      <button
        class="h-8 px-3 text-sm rounded-md bg-n-slate-3 disabled:opacity-40"
        :disabled="page >= totalPages"
        @click="goToPage(page + 1)"
      >
        {{ $t('AI_HANDOFFS_REPORT.NEXT') }}
      </button>
    </div>
  </div>
</template>
